import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Package, Truck, CheckCircle, Clock, XCircle, Loader2, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const CARRIER_URLS = {
  usps: 'https://tools.usps.com/go/TrackConfirmAction?tLabels=',
  ups: 'https://www.ups.com/track?tracknum=',
  fedex: 'https://www.fedex.com/fedextrack/?trknbr=',
  dhl: 'https://www.dhl.com/us-en/home/tracking.html?tracking-id=',
};

const CARRIER_NAMES = {
  usps: 'USPS', ups: 'UPS', fedex: 'FedEx', dhl: 'DHL', other: 'Carrier',
};

const STATUS_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'processing', label: 'Processing', icon: Package },
  { key: 'shipped', label: 'Shipped', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

const StatusTimeline = ({ status }) => {
  const statusIndex = STATUS_STEPS.findIndex(s => s.key === status);
  const isCancelled = status === 'cancelled';

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: '#fef2f2' }}>
        <XCircle className="h-5 w-5" style={{ color: '#dc2626' }} />
        <span className="text-sm font-semibold" style={{ color: '#dc2626' }}>Cancelled</span>
      </div>
    );
  }

  return (
    <div className="flex items-center w-full mt-4">
      {STATUS_STEPS.map((step, i) => {
        const isActive = i <= statusIndex;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center" style={{ minWidth: 60 }}>
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all"
                style={{
                  backgroundColor: isActive ? '#FF8C19' : '#e5d5c0',
                }}
              >
                <Icon className="h-4 w-4" style={{ color: isActive ? '#fff' : '#736c64' }} />
              </div>
              <span
                className="text-xs mt-1 text-center font-medium"
                style={{ color: isActive ? '#4a2c1a' : '#736c64' }}
              >
                {step.label}
              </span>
            </div>
            {i < STATUS_STEPS.length - 1 && (
              <div
                className="flex-1 h-1 rounded-full mx-1"
                style={{ backgroundColor: i < statusIndex ? '#FF8C19' : '#e5d5c0' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const OrderCard = ({ order }) => {
  const date = new Date(order.createdAt).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div
      data-testid={`order-card-${order.orderNumber}`}
      className="bg-white rounded-lg shadow-md p-6 mb-5 border-l-4 transition-all hover:shadow-lg"
      style={{ borderColor: '#FF8C19' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
        <div>
          <p className="text-lg font-bold" style={{ color: '#4a2c1a' }}>
            Order #{order.orderNumber}
          </p>
          <p className="text-sm" style={{ color: '#736c64' }}>{date}</p>
        </div>
        <p className="text-xl font-bold" style={{ color: '#FF8C19' }}>
          ${order.total.toFixed(2)}
        </p>
      </div>

      <div className="border-t pt-3 mb-3" style={{ borderColor: '#e5d5c0' }}>
        {order.items.map((item, idx) => (
          <div key={idx} className="flex justify-between py-1">
            <span className="text-sm" style={{ color: '#4a2c1a' }}>
              {item.name} ({item.size}) x{item.quantity}
              {item.variant && (
                <span className="block text-xs" style={{ color: '#FF8C19' }}>
                  {item.variant}
                </span>
              )}
            </span>
            <span className="text-sm font-medium" style={{ color: '#4a2c1a' }}>
              ${(item.price * item.quantity).toFixed(2)}
            </span>
          </div>
        ))}
      </div>

      <div className="text-sm flex flex-wrap gap-x-6 gap-y-1 mb-2" style={{ color: '#736c64' }}>
        <span>Subtotal: ${order.subtotal.toFixed(2)}</span>
        <span>Shipping: ${order.shipping.toFixed(2)}</span>
        <span>Tax: ${order.tax.toFixed(2)}</span>
      </div>

      <StatusTimeline status={order.status} />

      {/* Tracking Info */}
      {order.trackingNumber && (
        <div
          data-testid={`tracking-info-${order.orderNumber}`}
          className="mt-4 p-3 rounded-lg flex items-center justify-between"
          style={{ backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}
        >
          <div className="flex items-center">
            <Truck className="h-5 w-5 mr-2" style={{ color: '#2563eb' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: '#1e40af' }}>
                {CARRIER_NAMES[order.trackingCarrier] || 'Tracking'}: {order.trackingNumber}
              </p>
            </div>
          </div>
          {CARRIER_URLS[order.trackingCarrier] && (
            <a
              href={`${CARRIER_URLS[order.trackingCarrier]}${order.trackingNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold px-3 py-1 rounded-full text-white"
              style={{ backgroundColor: '#2563eb' }}
            >
              Track Package
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const OrderTracking = () => {
  const [email, setEmail] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError('');
    setOrders(null);

    try {
      const response = await fetch(`/api/orders/track/by-email?email=${encodeURIComponent(email.trim())}`);
      if (!response.ok) throw new Error('Failed to fetch orders');
      const data = await response.json();
      setOrders(data);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-2xl">
        {/* Header */}
        <div className="text-center mb-10">
          <Package className="h-14 w-14 mx-auto mb-4" style={{ color: '#FF8C19' }} />
          <h1
            data-testid="track-order-heading"
            className="text-4xl md:text-5xl font-bold mb-3"
            style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}
          >
            Track Your Order
          </h1>
          <p className="text-base" style={{ color: '#736c64' }}>
            Enter the email address you used at checkout to view your orders.
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: '#736c64' }} />
              <Input
                data-testid="track-order-email-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10"
              />
            </div>
            <Button
              data-testid="track-order-submit-btn"
              type="submit"
              disabled={loading}
              className="text-white font-semibold px-8"
              style={{ backgroundColor: '#FF8C19' }}
            >
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <><Search className="h-4 w-4 mr-2" /> Find Orders</>
              )}
            </Button>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-center">
            <p className="text-sm" style={{ color: '#dc2626' }}>{error}</p>
          </div>
        )}

        {/* Results */}
        {orders !== null && !loading && (
          orders.length > 0 ? (
            <div data-testid="order-results">
              <p className="text-sm font-medium mb-4" style={{ color: '#736c64' }}>
                Found {orders.length} order{orders.length !== 1 ? 's' : ''} for {email}
              </p>
              {orders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          ) : (
            <div data-testid="no-orders-found" className="bg-white rounded-lg shadow-md p-10 text-center">
              <Package className="h-16 w-16 mx-auto mb-4" style={{ color: '#e5d5c0' }} />
              <h2 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>
                No Orders Found
              </h2>
              <p className="mb-6" style={{ color: '#736c64' }}>
                We couldn't find any orders for that email. Double-check the address you used at checkout.
              </p>
              <Link to="/shop">
                <Button
                  className="text-white font-semibold px-8"
                  style={{ backgroundColor: '#FF8C19' }}
                >
                  Browse Our Shop
                </Button>
              </Link>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
